<template>
  <div class="app-currency-control app-modal-field">
    <multiselect
      v-model="selectedCurrencyOption"
      :options="currencyOptions"
      track-by="value"
      label="label"
      :show-labels="false"
      :searchable="true"
      :allow-empty="true"
      :disabled="disabled"
      placeholder=""
      class="app-modal-select"
    >
      <template #noResult>
        <span>Sin opción disponible</span>
      </template>
      <template #noOptions>
        <span>Sin opción disponible</span>
      </template>
    </multiselect>

    <div v-if="showExchangeRate" class="app-currency-control__rate-wrap">
      <template v-if="!isEditingExchangeRate">
        <button
          type="button"
          class="app-currency-control__rate-btn"
          :disabled="disabled"
          @click="startExchangeRateEdit"
        >
          TC: {{ exchangeRateLabel }}
        </button>
      </template>

      <template v-else>
        <div class="app-currency-control__rate-edit">
          <input
            v-model.number="exchangeRateDraft"
            type="number"
            min="0.0001"
            step="0.0001"
            :disabled="disabled"
            class="form-control app-currency-control__rate-input"
            placeholder="Tipo de cambio"
          />

          <button
            type="button"
            class="app-table-icon-btn app-currency-control__rate-action"
            title="Aplicar tipo de cambio"
            :disabled="disabled"
            @click="applyExchangeRateEdit"
          >
            <i class="fa fa-check" aria-hidden="true"></i>
          </button>

          <button
            type="button"
            class="app-table-icon-btn app-currency-control__rate-action"
            title="Cancelar edición"
            :disabled="disabled"
            @click="cancelExchangeRateEdit"
          >
            <i class="fa fa-times" aria-hidden="true"></i>
          </button>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import currenciesService from '@/services/currencies';
import { extractCollection } from '@/utils/dataNormalization';

export default {
  name: 'AppCurrencyControl',
  props: {
    modelValue: {
      type: String,
      default: 'MXN',
    },
    exchangeRate: {
      type: [Number, String],
      default: 1,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['update:modelValue', 'update:exchangeRate'],
  data() {
    return {
      currencyCatalog: [],
      isEditingExchangeRate: false,
      exchangeRateDraft: null,
    };
  },
  mounted() {
    this.loadCurrencyOptions();
  },
  computed: {
    currencyOptions() {
      if (this.currencyCatalog.length > 0) {
        return this.currencyCatalog.map((currency) => ({
          value: currency.code,
          label: currency.code,
        }));
      }

      return [{ value: 'MXN', label: 'MXN' }];
    },
    selectedCurrencyOption: {
      get() {
        const code = this.normalizeCurrencyCode(this.modelValue) || 'MXN';
        return this.currencyOptions.find((option) => option.value === code) || this.currencyOptions[0] || null;
      },
      set(option) {
        const rawValue = typeof option === 'string' ? option : option?.value;
        const code = this.normalizeCurrencyCode(rawValue) || 'MXN';
        this.applyCurrencyChange(code);
      },
    },
    showExchangeRate() {
      const currentCode = this.normalizeCurrencyCode(this.modelValue) || 'MXN';
      return currentCode !== 'MXN';
    },
    exchangeRateLabel() {
      const raw = Number(this.exchangeRate || 0);
      const fallback = this.showExchangeRate ? 0 : 1;
      const value = Number.isFinite(raw) && raw > 0 ? raw : fallback;
      return new Intl.NumberFormat('es-MX', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 4,
      }).format(value);
    },
  },
  methods: {
    normalizeCurrencyCode(value) {
      return String(value || '').trim().toUpperCase();
    },
    async loadCurrencyOptions() {
      try {
        const response = await currenciesService.selectors();
        const list = extractCollection(response);

        this.currencyCatalog = (Array.isArray(list) ? list : [])
          .map((item) => {
            const code = this.normalizeCurrencyCode(item?.code);
            const exchangeRate = Number(item?.exchange_rate || 0);

            if (code === '') {
              return null;
            }

            return {
              code,
              exchangeRate: Number.isFinite(exchangeRate) && exchangeRate > 0 ? exchangeRate : 1,
              isDefault: Number(item?.default || 0) === 1,
            };
          })
          .filter(Boolean);

        this.syncCurrencySelection();
      } catch (error) {
        this.currencyCatalog = [];
      }
    },
    resolveExchangeRateByCode(code) {
      const normalizedCode = this.normalizeCurrencyCode(code);
      if (normalizedCode === '') {
        return null;
      }

      const match = this.currencyCatalog.find((currency) => currency.code === normalizedCode);
      if (!match) {
        return null;
      }

      const parsed = Number(match.exchangeRate || 0);
      return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
    },
    applyCurrencyChange(code) {
      const normalized = this.normalizeCurrencyCode(code) || 'MXN';
      this.$emit('update:modelValue', normalized);
      this.isEditingExchangeRate = false;
      this.exchangeRateDraft = null;

      if (normalized === 'MXN') {
        this.$emit('update:exchangeRate', 1);
        return;
      }

      const selectedRate = this.resolveExchangeRateByCode(normalized);
      if (selectedRate !== null) {
        this.$emit('update:exchangeRate', selectedRate);
        return;
      }

      const currentRate = Number(this.exchangeRate || 0);
      if (Number.isFinite(currentRate) && currentRate > 0) {
        this.$emit('update:exchangeRate', currentRate);
        return;
      }

      this.$emit('update:exchangeRate', 1);
    },
    syncCurrencySelection() {
      if (this.currencyCatalog.length === 0) {
        return;
      }

      const currentCode = this.normalizeCurrencyCode(this.modelValue);
      const current = this.currencyCatalog.find((currency) => currency.code === currentCode);
      const fallback = this.currencyCatalog.find((currency) => currency.isDefault) || this.currencyCatalog[0];
      const selected = current || fallback;

      if (!selected) {
        return;
      }

      if (selected.code !== currentCode) {
        this.$emit('update:modelValue', selected.code);
      }

      const existingRate = Number(this.exchangeRate || 0);
      const hasExistingRate = Number.isFinite(existingRate) && existingRate > 0;

      if (selected.code === 'MXN') {
        this.$emit('update:exchangeRate', 1);
        return;
      }

      if (current && hasExistingRate) {
        return;
      }

      const selectedRate = this.resolveExchangeRateByCode(selected.code);
      if (selectedRate !== null) {
        this.$emit('update:exchangeRate', selectedRate);
      }
    },
    startExchangeRateEdit() {
      if (this.disabled) {
        return;
      }

      const current = Number(this.exchangeRate || 0);
      this.exchangeRateDraft = Number.isFinite(current) && current > 0 ? current : 1;
      this.isEditingExchangeRate = true;
    },
    applyExchangeRateEdit() {
      const parsed = Number(this.exchangeRateDraft || 0);
      if (!Number.isFinite(parsed) || parsed <= 0) {
        return;
      }

      this.$emit('update:exchangeRate', parsed);
      this.isEditingExchangeRate = false;
      this.exchangeRateDraft = null;
    },
    cancelExchangeRateEdit() {
      this.isEditingExchangeRate = false;
      this.exchangeRateDraft = null;
    },
  },
};
</script>

<style scoped lang="scss" src="@/assets/scss/components/sales/_app-currency-control.scss"></style>
